/* eslint-disable camelcase */
import _ from 'underscore';
import config from '@bingads-webui-component-legacy/config/index';
import { i18nMap } from '@bingads-webui-campaign/report-shared';
import {
  Shared,
  Columns,
  AccountPerformanceReportUIUnifiedPreview,
  AccountPerformanceReport_ShareOfVoiceUIUnifiedPreview,
  AdGroupPerformanceReportUIUnifiedPreview,
  AdPerformanceReportUIUnifiedPreview,
  CampaignPerformanceReportUIUnifiedPreview,
  DestinationURLPerformanceReportUIUnifiedPreview,
  GeographicPerformanceReportUIUnifiedPreview,
  KeywordPerformanceReportUIUnifiedPreview,
  LabelAccountReportUIUnifiedPreview,
  LabelAdGroupReportUIUnifiedPreview,
  LabelAdReportUIUnifiedPreview,
  LabelCampaignReportUIUnifiedPreview,
  LabelKeywordReportUIUnifiedPreview,
  ProductDimensionPerformanceReportUIUnifiedPreview,
  ProductPartitionPerformanceReportUIUnifiedPreview,
  ProductPartitionUnitPerformanceReportUIUnifiedPreview,
  PublisherUsagePerformanceReportUIUnifiedPreview,
  ShareOfVoiceReportUIUnifiedPreview,
  UserLocationPerformanceReportUIUnifiedPreview,
} from '@bingads-webui/report-ui-interface-unified-preview';
import { elevatedColumnsMap } from './columns/elevated-columns';
import { restrictedColumnsByReport, defaultColumnsByReport, restrictedFilterColumnsByReport, restrictedDisplayColumnsByReport } from './columns/piloted-columns';
import { helpLinkIds, helpLinkIdsByReportType } from './columns/help-link-ids';
import { missingMandatoryColumnErrors } from './columns/missing-mandatory-column-error';
import { processSchema } from './schema-processor';
import { timeRangeOverride } from './overrides/time-range-override';

const dynamicPermissions = _.result(config.get('permissions'), 'dynamic', {});
const { IsRenameHotelToLodgingEnabled, CPSReporting } = dynamicPermissions;

if (IsRenameHotelToLodgingEnabled) {
  i18nMap.ColumnName.AdvertiserHotelId = _TL_('Advertiser property ID');
  i18nMap.ColumnName.CampaignHotelId = _TL_('Campaign property ID');
  i18nMap.ColumnName.HotelGroup = _TL_('Property group');
  i18nMap.ColumnName.HotelGroupNodeId = _TL_('Property group node id');
  i18nMap.ColumnName.SourceHotelId = _TL_('Source property ID');
  i18nMap.ColumnName.HotelName = _TL_('Property name');
  helpLinkIds.CampaignHotelId = 'pop_BA_Reporting_Clarity_CampaignPropertyId';
  helpLinkIds.AdvertiserHotelId = 'pop_BA_Reporting_Clarity_AdvertiserPropertyId';
  helpLinkIds.HotelGroup = 'pop_BA_Reporting_Clarity_PropertyGroup';
  helpLinkIds.HotelGroupNodeId = 'pop_BA_Reporting_Clarity_PropertyGroupNodeId';
}

/* istanbul ignore if  */
if (CPSReporting) {
  i18nMap.ColumnName.CostPerSale = _TL_('Avg. CPS');
  i18nMap.ColumnName.Sales = _TL_('Sales Count');
}

const process = _.memoize(
  rawSchema =>
    processSchema(
      rawSchema,
      Shared,
      elevatedColumnsMap[rawSchema.name],
      restrictedColumnsByReport[rawSchema.name] || [],
      defaultColumnsByReport[rawSchema.name] || [],
      i18nMap.ColumnName,
      { ...helpLinkIds, ...helpLinkIdsByReportType[rawSchema.name] },
      timeRangeOverride[rawSchema.name],
      missingMandatoryColumnErrors[rawSchema.name],
      restrictedFilterColumnsByReport[rawSchema.name] || [],
      restrictedDisplayColumnsByReport[rawSchema.name] || []
    ),
  reportSchema => reportSchema.name
);

/* eslint-disable max-len */
const AccountPerformanceReport = () => process(AccountPerformanceReportUIUnifiedPreview);
const AccountPerformanceReport_ShareOfVoice = () => process(AccountPerformanceReport_ShareOfVoiceUIUnifiedPreview);
const AdGroupPerformanceReport = () => process(AdGroupPerformanceReportUIUnifiedPreview);
const AdPerformanceReport = () => process(AdPerformanceReportUIUnifiedPreview);
const CampaignPerformanceReport = () => process(CampaignPerformanceReportUIUnifiedPreview);
const DestinationURLPerformanceReport = () => process(DestinationURLPerformanceReportUIUnifiedPreview);
const GeographicPerformanceReport = () => process(GeographicPerformanceReportUIUnifiedPreview);
const KeywordPerformanceReport = () => process(KeywordPerformanceReportUIUnifiedPreview);
const ProductDimensionPerformanceReport = () => process(ProductDimensionPerformanceReportUIUnifiedPreview);
const ProductPartitionPerformanceReport = () => process(ProductPartitionPerformanceReportUIUnifiedPreview);
const ProductPartitionUnitPerformanceReport = () => process(ProductPartitionUnitPerformanceReportUIUnifiedPreview);
const PublisherUsagePerformanceReport = () => process(PublisherUsagePerformanceReportUIUnifiedPreview);
const ShareOfVoiceReport = () => process(ShareOfVoiceReportUIUnifiedPreview);
const UserLocationPerformanceReport = () => process(UserLocationPerformanceReportUIUnifiedPreview);
const LabelCampaignReport = () => process(LabelCampaignReportUIUnifiedPreview);
const LabelAdGroupReport = () => process(LabelAdGroupReportUIUnifiedPreview);
const LabelAccountReport = () => process(LabelAccountReportUIUnifiedPreview);
const LabelKeywordReport = () => process(LabelKeywordReportUIUnifiedPreview);
const LabelAdReport = () => process(LabelAdReportUIUnifiedPreview);

export const unifiedSchema = {};
Object.defineProperties(unifiedSchema, {
  AccountPerformanceReport: { get: AccountPerformanceReport, enumerable: true },
  'AccountPerformanceReport-ShareOfVoice': { get: AccountPerformanceReport_ShareOfVoice, enumerable: true },
  AdGroupPerformanceReport: { get: AdGroupPerformanceReport, enumerable: true },
  AdPerformanceReport: { get: AdPerformanceReport, enumerable: true },
  CampaignPerformanceReport: { get: CampaignPerformanceReport, enumerable: true },
  DestinationURLPerformanceReport: { get: DestinationURLPerformanceReport, enumerable: true },
  GeographicPerformanceReport: { get: GeographicPerformanceReport, enumerable: true },
  KeywordPerformanceReport: { get: KeywordPerformanceReport, enumerable: true },
  ProductDimensionPerformanceReport: { get: ProductDimensionPerformanceReport, enumerable: true },
  ProductPartitionPerformanceReport: { get: ProductPartitionPerformanceReport, enumerable: true },
  ProductPartitionUnitPerformanceReport: { get: ProductPartitionUnitPerformanceReport, enumerable: true },
  PublisherUsagePerformanceReport: { get: PublisherUsagePerformanceReport, enumerable: true },
  ShareOfVoiceReport: { get: ShareOfVoiceReport, enumerable: true },
  UserLocationPerformanceReport: { get: UserLocationPerformanceReport, enumerable: true },
  LabelCampaignReport: { get: LabelCampaignReport, enumerable: true },
  LabelAdGroupReport: { get: LabelAdGroupReport, enumerable: true },
  LabelAccountReport: { get: LabelAccountReport, enumerable: true },
  LabelKeywordReport: { get: LabelKeywordReport, enumerable: true },
  LabelAdReport: { get: LabelAdReport, enumerable: true },
  ColumnConfig: { value: Columns.columns },
});
